import {BaseHost} from "./base-host";
import * as cmmn from '@utils/index'
import {logger} from "@services/logger/logger.service";
import { IDocumentChangeHandler, IOfficeHost } from "@core/shared/office-hosts/office-host.types";

const CUSTOM_ERROR_CODE = -1;

export class OutlookHost extends BaseHost implements IOfficeHost
{
    // @ts-ignore
    private item: Office.MessageCompose & Office.MessageRead;
    private isItemChanged: boolean = false;
    private onDocumentChangeHandler: IDocumentChangeHandler | null = null

    constructor() {
        super()
        Office.onReady((info) => {
            if (info.host !== Office.HostType.Outlook) {
                throw new Error(`invalid host type: ${info.host}`);
            }

            if(!Office.context.mailbox) {
                logger.log('Run inside Outlook to be able to use it.');
                return;
            }

            Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, (event: any) => {
                if (event.type == Office.EventType.ItemChanged) {
                    this.onItemChanged(event)
                }
            }, {}, (result: Office.AsyncResult<void>) => {
                if (result.status == Office.AsyncResultStatus.Failed) {
                    logger.error("addHandlerAsync(ItemChanged) error: " + JSON.stringify(result.error));
                }
            });

            if (Office.context.mailbox?.item) {
                this.item = Office.context.mailbox.item;
            }
        });
    }

    public override async onDocumentChange(handler: IDocumentChangeHandler): Promise<void> {
        this.onDocumentChangeHandler = handler;
    }

    private async onItemChanged(result: any) {
        if (Office.context.mailbox?.item) {
            this.item = Office.context.mailbox.item;
        }
        this.isItemChanged = true;

        result.documentID = await this.getDocumentIDAsync();
        // this.eventListeners.get(Office.EventType.ItemChanged)?.map(async (l) => await l(result));
        if (this.onDocumentChangeHandler) {
            this.onDocumentChangeHandler()
        }
    }

    public override async getDocumentIDAsync(): Promise<string>
    {
        if (!this.item) {
            throw new Error('Item not found');
        }

        let isSuccess = false;
        let messageId;

        if (!isSuccess) {
            messageId = this.item.itemId;
            if (messageId) isSuccess = true;
        }

        if (!isSuccess && this.item.getItemIdAsync) {
            isSuccess = await cmmn.callAsync<boolean>(async (r) => {
                this.item.getItemIdAsync((idResult: Office.AsyncResult<string>) => {
                    if (idResult.status == Office.AsyncResultStatus.Failed) {
                        return r.next(false);
                    }

                    messageId = idResult.value;
                    return r.next(true);
                })
            });
        }

        if (!isSuccess && this.item.saveAsync) {
            isSuccess = await cmmn.callAsync<boolean>(async (r) => {
                this.item.saveAsync((saveResult: Office.AsyncResult<string>) => {
                    if (saveResult.status == Office.AsyncResultStatus.Failed) {
                        return r.next(false);
                    }

                    messageId = saveResult.value;
                    return r.next(true);
                })
            });
        }

        if (!isSuccess) {
            messageId = this.item.internetMessageId;
            if (messageId) isSuccess = true;
        }

        if (isSuccess) {
            // @ts-ignore
            return messageId
        };

        throw new Error(`[getDocumentIDAsync]: ${isSuccess}`)
    }

    async setMessageComposeSelectedText(text: string,
                                        success: () => void,
                                        failed: (error: Office.Error) => void
    ): Promise<boolean> {
        this.isItemChanged = false;

        return await cmmn.callAsync<boolean>(async (r) => {
            if (!this.item.body.getTypeAsync) {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message being compose must contain the 'getTypeAsync' function`,
                    name: 'Invalid MessageCompose item'
                });
                return r.next(false);
            }

            this.item.body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) => {
                if (this.isItemChanged) {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the item was changed before the selected text was set`,
                        name: 'ItemChanged event raized'
                    });
                    return r.next(false);
                }

                if (typeResult.status == Office.AsyncResultStatus.Failed) {
                    failed(typeResult.error);
                    return r.next(false);
                }

                if (!this.item.setSelectedDataAsync) {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the message being compose must contain the 'setSelectedDataAsync' function`,
                        name: 'Invalid MessageCompose item'
                    });
                    return r.next(false);
                }

                let newData = text;
                if (typeResult.value == Office.CoercionType.Html) {
                    newData = text.replaceAll('\n', '<br />');
                }

                this.item.setSelectedDataAsync(newData, { coercionType: typeResult.value }, (selectedResult: Office.AsyncResult<any>) => {
                    if (this.isItemChanged) {
                        failed({
                            code: CUSTOM_ERROR_CODE,
                            message: `the item was changed before the selected text was set`,
                            name: 'ItemChanged event raized'
                        });
                        return r.next(false);
                    }

                    if (selectedResult.status === Office.AsyncResultStatus.Failed) {
                        failed(selectedResult.error);
                        return r.next(false);
                    }

                    success();
                    return r.next(true);
                });
            });
        });
    }

    async getMessageComposeSelectedText(
        success: (result: string) => void,
        failed: (error: Office.Error) => void
    ): Promise<boolean> {
        this.isItemChanged = false;

        return await cmmn.callAsync<boolean>(async (r) => {
            if (!this.item.body.getTypeAsync) {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message being compose must contain the 'getTypeAsync' function`,
                    name: 'Invalid MessageCompose item'
                });
                return r.next(false);
            }

            this.item.body.getTypeAsync((typeResult: Office.AsyncResult<Office.CoercionType>) => {
                if (this.isItemChanged) {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `ItemChanged event raized: the item was changed before the type was defined`,
                        name: 'GetSelectedText failed'
                    });
                    return r.next(false);
                }

                if (typeResult.status == Office.AsyncResultStatus.Failed) {
                    failed(typeResult.error);
                    return r.next(false);
                }

                if (!this.item.getSelectedDataAsync) {
                    failed({
                        code: CUSTOM_ERROR_CODE,
                        message: `the message being compose must contain the 'getSelectedDataAsync' function`,
                        name: 'GetSelectedText failed'
                    });
                    return r.next(false);
                }

                this.item.getSelectedDataAsync(Office.CoercionType.Text, (selectedResult: Office.AsyncResult<any>) => {
                    if (this.isItemChanged) {
                        failed({
                            code: CUSTOM_ERROR_CODE,
                            message: `ItemChanged event raized: the item was changed before the selected text was read`,
                            name: 'GetSelectedText failed'
                        });
                        return r.next(false);
                    }

                    if (selectedResult.status === Office.AsyncResultStatus.Failed) {
                        failed(selectedResult.error);
                        return r.next(false);
                    }

                    let selectedText;

                    switch (typeResult.value) {
                        case Office.CoercionType.Html:
                            selectedText = <string>selectedResult.value.data;
                            logger.log("getMessageComposeSelectedText() success: html[" + selectedText?.length + "]")
                            break;

                        case Office.CoercionType.Text:
                            selectedText = <string>selectedResult.value.data;
                            logger.log("getMessageComposeSelectedText() success: text[" + selectedText?.length + "]")
                            break;

                        default:
                            failed({
                                code: CUSTOM_ERROR_CODE,
                                message: 'Invalid Type: selected type unknown: ' + typeResult.value,
                                name: 'GetSelectedText failed'
                            });
                            return r.next(false);
                    }

                    success(selectedText);
                    return r.next(true);
                });
            });
        });
    }

    public override checkSelectable(): boolean {
        /// TODO: need to check getSelectedEntities() function:
        /// https://learn.microsoft.com/en-us/javascript/api/outlook/office.messageread?view=outlook-js-preview#outlook-office-messageread-getselectedentities-member(1)
        return this.item?.body.getTypeAsync != undefined;
    }

    public override checkEditable(): boolean {
        return this.item?.displayReplyForm == undefined;
    }

    public override async getSelectedContentAsync(): Promise<string> {
        let selectedText = '';

        try {
            await this.getMessageComposeSelectedText(
                (result) => { selectedText = result; },
                (error) => { logger.error(error.message); }
            );
        }
        catch (err) {
            if (err instanceof Error) {
                logger.error(`getSelectedContentAsync() error: ${err?.message ?? JSON.stringify(err)}`);
            } else {
                logger.error(err);
            }
        }

        return selectedText;
    }

    public override async setSelectedTextAsync(plainText: string): Promise<void> {
        try {
            await this.setMessageComposeSelectedText(
                plainText,
                () => { },
                (error) => logger.error(error.message)
            );
        }
        catch (err) {
            if (err instanceof Error) {
                logger.error(this, `setSelectedTextAsync() error: ${err['message'] ?? JSON.stringify(err)}`);
            } else {
                logger.error(err);
            }
        }

        logger.log("setSelectedTextAsync() success: text[" + plainText.length + "]")
    }

    public override checkDocumentReadable(): boolean {
        return Boolean(this.item);
    }

    public override async attachImageToEmail(base64Image: string, filename: string): Promise<void> {
        this.item.addFileAttachmentAsync(
            base64Image,
            filename,
            { isInline: false },
            (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    logger.log('Attachment added successfully');

                    this.item.getAttachmentsAsync(function (asyncResult) {
                        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                            const attachments = asyncResult.value;
                            logger.log(attachments);
                        } else {
                            logger.error("Error retrieving attachments: " + asyncResult.error.message);
                        }
                    });
                } else {
                    logger.error('Error adding attachment: ' + result.error.message);
                }
            }
        );
    }

    async getMessageText(
        success: (result: string) => void,
        failed: (error: Office.Error) => void
    ): Promise<boolean> {
        return await cmmn.callAsync<boolean>(async (r) => {
            if (!this.item.body.getAsync) {
                failed({
                    code: CUSTOM_ERROR_CODE,
                    message: `the message must contain the 'getAsync' function`,
                    name: 'GetMessageText failed'
                });
                return r.next(false);
            }

            this.item.body.getAsync(
                Office.CoercionType.Text,
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        failed(result.error);
                        return r.next(false);
                    }

                    success(<string>result.value);
                    return r.next(true);
                }
            );
        });
    }

    public override async getDocumentTextAsync(): Promise<string> {
        let bodyText = '';

        try {
            await this.getMessageText(
                (result) => { bodyText = result; },
                (error) => { logger.error(error.message); }
            );

            logger.log("getMailBodyAsync[" + bodyText?.length + "]")
        }
        catch (err) {
            if (err instanceof Error) {
                logger.error(this, `getMailBodyAsync() error: ${err['message'] ?? JSON.stringify(err)}`);
            } else {
                logger.error(err);
            }
        }

        return bodyText;
    }

    public override async setSelectedImageAsync(fileName: string, b64Text: string): Promise<void> {
        await cmmn.callAsync<boolean>(async (r) => {
            this.item.body.getAsync(Office.CoercionType.Html, (bodyResult: Office.AsyncResult<string>) => {
                if (this.isItemChanged) {
                    logger.error(`the item was changed while getting body`);
                    return r.next(false);
                }

                if (bodyResult.status === Office.AsyncResultStatus.Failed) {
                    logger.error(bodyResult.error.message);
                    return r.next(false);
                }

                const options = { isInline: true, asyncContext: bodyResult.value };

                this.item.addFileAttachmentFromBase64Async(b64Text, fileName, options, (attachResult: Office.AsyncResult<string>) => {
                    if (this.isItemChanged) {
                        logger.error(`the item was changed while attachment creating`);
                        return r.next(false);
                    }

                    if (attachResult.status == Office.AsyncResultStatus.Failed) {
                        logger.error(attachResult.error.message);
                        return r.next(false);
                    }

                    //let body = attachResult.asyncContext;
                    //body = body.replace("<p class=MsoNormal>", `<p class=MsoNormal><img src="cid:${fileName}">`);

                    const imgHtml = `<img src="cid:${fileName}" />`;

                    this.item.setSelectedDataAsync(imgHtml, { coercionType: Office.CoercionType.Html }, (selectResult: Office.AsyncResult<any>) => {
                        if (this.isItemChanged) {
                            logger.error(`the item was changed while image inserting`);
                            return r.next(false);
                        }

                        if (selectResult.status == Office.AsyncResultStatus.Failed) {
                            logger.error(selectResult.error.message);
                            return r.next(false);
                        }

                        return r.next(true);
                    });
                });
            });
        });
    }
}
